import { z } from "zod";
import { zKeyedRecord } from "./zod/utils.js";

import { EnumValues, ProjectLanguage } from "./enums.js";
import { assertNever } from "./errors.js";
import {
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  UserId,
} from "./idTypeBrands.js";
import { DATA_APP_JS_MONACO_LANG_ID } from "./dataAppTypes.js";
import { GUIDE_MARKDOWN_MONACO_LANG_ID } from "./org-guides/guideMentionUtils.js";
import { getNormalEnum } from "./pseudoEnums.js";

// Data connection details keys
export const ConnectionDetailsKeys = {
  AUTHENTICATION_TYPE_KEY: "authenticationType",
  IAM_ROLE_TRUST_POLICY_PROPERTY: "iamRoleTrustPolicy",
  DBT_SEMANTIC_LAYER_VERSION_KEY: "dbtSemanticLayerVersion",
  EXTERNAL_ID_PROPERTY: "externalId",
  OAUTH_ROLE_KEY: "oAuthRole",
  ROLE_ARN_PROPERTY: "roleArn",
  SESSION_TOKEN_PROPERTY: "sessionToken",
  SNOWFLAKE_OAUTH_CONNECTION_ID_KEY: "snowflakeOAuthConnectionId",
  USE_DBT_JDBC_DRIVER_KEY: "useDbtJdbcDriver",
};

export const SchemaRefreshQueuedReasonLiteral = z.enum([
  "GLOBAL_RATE_LIMIT",
  "ORG_RATE_LIMIT",
  "QUEUE_EXISTS",
  "ALREADY_RUNNING",
]);
export type SchemaRefreshQueuedReason = z.infer<
  typeof SchemaRefreshQueuedReasonLiteral
>;
export const SchemaRefreshQueuedReason = getNormalEnum(
  SchemaRefreshQueuedReasonLiteral,
);

export const SchemaRefreshSourceLiteral = z.enum(["manual", "scheduled"]);
export type SchemaRefreshSource = z.infer<typeof SchemaRefreshSourceLiteral>;
export const SchemaRefreshSource = getNormalEnum(SchemaRefreshSourceLiteral);

export const SchemaRefreshKindLiteral = z.enum(["FULL", "PARTIAL"]);
export type SchemaRefreshKind = z.infer<typeof SchemaRefreshKindLiteral>;
export const SchemaRefreshKind = getNormalEnum(SchemaRefreshKindLiteral);

export const SchemaRefreshTriggerTypeLiteral = z.enum([
  "USER",
  "SCHEDULED",
  "SYSTEM",
]);
export type SchemaRefreshTriggerType = z.infer<
  typeof SchemaRefreshTriggerTypeLiteral
>;
export const SchemaRefreshTriggerType = getNormalEnum(
  SchemaRefreshTriggerTypeLiteral,
);

// A refresh is USER-triggered when it carries a user, otherwise it's SCHEDULED.
// Never SYSTEM, so the return type is narrowed to the two values it can produce.
export function schemaRefreshTriggerTypeForUser(
  userId: UserId | null | undefined,
):
  | typeof SchemaRefreshTriggerType.USER
  | typeof SchemaRefreshTriggerType.SCHEDULED {
  return userId != null
    ? SchemaRefreshTriggerType.USER
    : SchemaRefreshTriggerType.SCHEDULED;
}

export const SchemaRefreshLifecycleStatusLiteral = z.enum([
  "QUEUED",
  "IN_PROGRESS",
  "COMPLETED",
  "LOST",
]);
export type SchemaRefreshLifecycleStatus = z.infer<
  typeof SchemaRefreshLifecycleStatusLiteral
>;
export const SchemaRefreshLifecycleStatus = getNormalEnum(
  SchemaRefreshLifecycleStatusLiteral,
);

export const SchemaRefreshOutcomeLiteral = z.enum([
  "SUCCESS",
  "PARTIAL_SUCCESS",
  "FAILURE",
  "CANCELLED",
  "SKIPPED",
]);
export type SchemaRefreshOutcome = z.infer<typeof SchemaRefreshOutcomeLiteral>;
export const SchemaRefreshOutcome = getNormalEnum(SchemaRefreshOutcomeLiteral);

export const SchemaRefreshErrorCategoryLiteral = z.enum([
  "USER_ACTIONABLE",
  "TRANSIENT",
  "INTERNAL",
  "RESOURCE_LIMIT",
  "CANCELLED",
  "OAUTH_TOKEN_EXPIRED",
]);
export type SchemaRefreshErrorCategory = z.infer<
  typeof SchemaRefreshErrorCategoryLiteral
>;
export const SchemaRefreshErrorCategory = getNormalEnum(
  SchemaRefreshErrorCategoryLiteral,
);

export const SchemaRefreshPartialFailuresS3UploadStateLiteral = z.enum([
  "NOT_STARTED",
  "UPLOADING",
  "UPLOADED",
  "FAILED",
]);
export type SchemaRefreshPartialFailuresS3UploadState = z.infer<
  typeof SchemaRefreshPartialFailuresS3UploadStateLiteral
>;
export const SchemaRefreshPartialFailuresS3UploadState = getNormalEnum(
  SchemaRefreshPartialFailuresS3UploadStateLiteral,
);

// The warehouse traversal level a DFS partial failure is attributable to.
export const SchemaRefreshPartialFailureLevelLiteral = z.enum([
  "DATABASE",
  "SCHEMA",
  "TABLE",
  "COLUMN",
]);
export type SchemaRefreshPartialFailureLevel = z.infer<
  typeof SchemaRefreshPartialFailureLevelLiteral
>;
export const SchemaRefreshPartialFailureLevel = getNormalEnum(
  SchemaRefreshPartialFailureLevelLiteral,
);

export const SqlCompletionTypeLiteral = z.enum([
  "table",
  "column",
  "schema",
  "database",
  "keyword",
  "identifier",
]);
export type SqlCompletionType = z.infer<typeof SqlCompletionTypeLiteral>;

// Keep in sync with DataConnectionApiType in apiTypes.ts
export const DataConnectionTypeLiteral = z.enum([
  "athena",
  "postgres",
  "prestodb",
  "snowflake",
  "redshift",
  "bigquery",
  "sqlserver",
  "mysql",
  "mariadb",
  "spark",
  "transform",
  "trino",
  "dremio",
  "databricks",
  "clickhouse",
  "alloydb",
  "starburst",
  "materialize",
  "cloudsql",
  "cloudsql__mysql",
  "cloudsql__postgres",
  "cloudsql__sqlserver",
  "rds",
  "rds__mysql",
  "rds__postgres",
  "motherduck",
]);
export type DataConnectionType = z.infer<typeof DataConnectionTypeLiteral>;
export const DataConnectionType = getNormalEnum(DataConnectionTypeLiteral);

export const humanReadableDataConnectionType = (
  type: DataConnectionType,
): string => {
  switch (type) {
    case "prestodb":
      return "PrestoDB";
    case "bigquery":
      return "BigQuery";
    case "sqlserver":
      return "SQL Server";
    case "mysql":
      return "MySQL";
    case "mariadb":
      return "MariaDB";
    case "clickhouse":
      return "ClickHouse";
    case "alloydb":
      return "AlloyDB";
    case "cloudsql":
    case "cloudsql__mysql":
    case "cloudsql__postgres":
    case "cloudsql__sqlserver":
      return "Cloud SQL";
    case "rds":
    case "rds__mysql":
    case "rds__postgres":
      return "Amazon RDS";
    case "motherduck":
      return "Motherduck";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

export const preferredConnectionTypes: DataConnectionType[] = [
  DataConnectionType.snowflake,
  DataConnectionType.postgres,
  DataConnectionType.bigquery,
  DataConnectionType.mysql,
  DataConnectionType.redshift,
  DataConnectionType.athena,
  DataConnectionType.databricks,
  DataConnectionType.clickhouse,
];

export const OAuthConnectionTypeLiteral = z.enum([
  "bigquery",
  "databricks",
  "snowflake",
]);
export type OAuthConnectionType = z.infer<typeof OAuthConnectionTypeLiteral>;
export const OAuthConnectionType = getNormalEnum(OAuthConnectionTypeLiteral);

export const oAuthConnectionTypes: DataConnectionType[] = [
  DataConnectionType.bigquery,
  DataConnectionType.databricks,
  DataConnectionType.snowflake,
];

export const perUserCredsConnectionTypes: DataConnectionType[] = [
  ...oAuthConnectionTypes,
  DataConnectionType.trino,
];

export const oAuthSchemaSupportedTypes: DataConnectionType[] = [
  DataConnectionType.snowflake,
];

// Connection types that support the delta schema refresh path. The rest fall
// back to a full refresh.
export const deltaSchemaRefreshSupportedTypes: DataConnectionType[] = [
  DataConnectionType.snowflake,
];

//  Any db connection that by default has TLS/SSL enabled, without customer configuration / enablement.
//  This does not include data connections, such as sqlserver, that must "enable encryption" for SLS/TLS.
export const hasTLSEnabled = (connectionType: DataConnectionType): boolean => {
  switch (connectionType) {
    case "athena":
    case "bigquery":
    case "databricks":
    case "motherduck":
    case "redshift":
    case "snowflake":
    case "trino":
      return true;
    case "clickhouse":
    case "dremio":
    case "mariadb":
    case "mysql":
    case "postgres":
    case "prestodb":
    case "spark":
    case "sqlserver":
    case "transform":
    case "alloydb":
    case "starburst":
    case "materialize":
    case "cloudsql":
    case "cloudsql__postgres":
    case "cloudsql__mysql":
    case "cloudsql__sqlserver":
    case "rds":
      return false;
    case "rds__postgres":
    case "rds__mysql":
      return true;
    default:
      assertNever(connectionType, connectionType);
  }
};

const DataConnectionStatusLiteral = z.enum([
  "refreshing",
  "refresh-enqueued",
  "errored",
  "fresh",
]);

export type DataConnectionStatus = z.infer<typeof DataConnectionStatusLiteral>;
export const DataConnectionStatus = getNormalEnum(DataConnectionStatusLiteral);

export const MONACO_SQL_LANGUAGE_TYPES = [
  "pgsql",
  "mysql",
  "redshift",
  "sql",
  "snowflake",
] as const;
// keep in sync with MonacoWebpackPlugin config in our webpack config
export type MonacoLanguageType =
  | (typeof MONACO_SQL_LANGUAGE_TYPES)[number]
  | "python"
  | "r"
  | "markdown"
  | "yaml"
  | "json"
  | "css"
  | "javascript"
  | "plaintext"
  | typeof GUIDE_MARKDOWN_MONACO_LANG_ID // doesn't need to be added to MonacoWebpackPlugin because it's a custom language
  | typeof DATA_APP_JS_MONACO_LANG_ID;

export const ProjectLanguageMonacoLanguageMap = {
  [ProjectLanguage.R]: "r" as const,
  [ProjectLanguage.PYTHON]: "python" as const,
};

export const DbtTestStatus = z.enum([
  "pass",
  "fail",
  "warn",
  "error",
  "skipped",
]);
export type DbtTestStatus = z.infer<typeof DbtTestStatus>;

export const DbtTestMetadata = z.object({
  name: z.string(),
  state: DbtTestStatus,
  // column name is null when the test is across multiple columns
  columnName: z.string().or(z.null()),
});
export type DbtTestMetadata = z.infer<typeof DbtTestMetadata>;

export const DbtTestMetadataList = z.array(DbtTestMetadata);
export type DbtTestMetadataList = z.infer<typeof DbtTestMetadataList>;

export const DbtColumnMetadata = z.object({
  name: z.string(),
  description: z.string().or(z.null()),
});

export const ProcessedDbtColumnMetadata = z.object({
  dbtTests: DbtTestMetadataList.or(z.null()),
  dbtDescription: z.string().or(z.null()),
  dbtDescriptionMdStripped: z.string().or(z.null()),
});

export type ProcessedDbtColumnMetadata = z.infer<
  typeof ProcessedDbtColumnMetadata
>;

export type DbtColumnMetadata = z.infer<typeof DbtColumnMetadata>;

export const DbtColumnMetadataList = z.array(DbtColumnMetadata);
export type DbtColumnMetadataList = z.infer<typeof DbtColumnMetadataList>;

const DbtJobMetadata = z.object({
  id: z.number(),
});
type DbtJobMetadata = z.infer<typeof DbtJobMetadata>;

export const DbtJobMetadataList = z.array(DbtJobMetadata);
export type DbtJobMetadataList = z.infer<typeof DbtJobMetadataList>;

export const DbtMetadata = z.object({
  dbtAccessUrl: z.string(),
  dbtRunId: z.string().or(z.null()),
  dbtAccountId: z.string(),
  dbtJobId: z.string().or(z.null()),
  dbtProjectId: z.string(),
  dbtEnvironmentId: z.string().or(z.null()).optional(),
  database: z.string(),
  schema: z.string(),
  table: z.string(),
  dbtUniqueId: z.string(),
  // eslint-disable-next-line hex/no-non-json-zod-in-wire-schema -- the same `InstanceOf(Date)` check the runtypes schema made; these are in-process Date values, not a JSON payload
  dbtLastRefreshed: z.union([z.null(), z.instanceof(Date)]),
  // eslint-disable-next-line hex/no-non-json-zod-in-wire-schema -- the same `InstanceOf(Date)` check the runtypes schema made; these are in-process Date values, not a JSON payload
  dbtMetadataLastChecked: z.instanceof(Date),
  dbtMetadataError: z.boolean(),
  dbtDescription: z.string().or(z.null()),
  dbtDescriptionMdStripped: z.string().or(z.null()),
  dbtColumns: DbtColumnMetadataList,
});
export type DbtMetadata = z.infer<typeof DbtMetadata>;

export const DbtModelMetadata = DbtMetadata.extend({
  dbtTests: DbtTestMetadataList,
});
export type DbtModelMetadata = z.infer<typeof DbtModelMetadata>;

export const DbtSourceMetadata = DbtMetadata.extend({
  dbtFreshness: z.string().or(z.null()),
  dbtWarnAfterCount: z.number().or(z.null()),
  dbtWarnAfterPeriod: z.string().or(z.null()),
  dbtErrorAfterCount: z.number().or(z.null()),
  dbtErrorAfterPeriod: z.string().or(z.null()),
});
export type DbtSourceMetadata = z.infer<typeof DbtSourceMetadata>;

export const MinimalDbtModelMetadata = DbtModelMetadata.pick({
  dbtLastRefreshed: true,
  dbtTests: true,
  dbtMetadataLastChecked: true,
  dbtRunId: true,
  dbtDescription: true,
  dbtDescriptionMdStripped: true,
  dbtColumns: true,
});
export type MinimalDbtModelMetadata = z.infer<typeof MinimalDbtModelMetadata>;

export const MinimalDbtSourceMetadata = DbtSourceMetadata.pick({
  dbtLastRefreshed: true,
  dbtFreshness: true,
  dbtMetadataLastChecked: true,
  dbtWarnAfterCount: true,
  dbtWarnAfterPeriod: true,
  dbtErrorAfterCount: true,
  dbtErrorAfterPeriod: true,
  dbtRunId: true,
  dbtDescription: true,
  dbtDescriptionMdStripped: true,
  dbtColumns: true,
});
export type MinimalDbtSourceMetadata = z.infer<typeof MinimalDbtSourceMetadata>;

export const DbtErrorResponse = z.object({
  dbtMetadataError: z.boolean(),
});
export type DbtErrorResponse = z.infer<typeof DbtErrorResponse>;

export const DbtMetricTimeGrains = z.array(z.string());
export type DbtMetricTimeGrains = z.infer<typeof DbtMetricTimeGrains>;

export const DbtMetricDimensions = z.array(z.string());
export type DbtMetricDimensions = z.infer<typeof DbtMetricDimensions>;

export const DbtMetricDimensionV2TypeLiteral = z.enum(["CATEGORICAL", "TIME"]);
export type DbtMetricDimensionV2Type = z.infer<
  typeof DbtMetricDimensionV2TypeLiteral
>;
export const DbtMetricDimensionV2Type = getNormalEnum(
  DbtMetricDimensionV2TypeLiteral,
);

export const DbtMetricDimensionV2 = z
  .object({
    name: z.string(),
    description: z.string().or(z.null()),
    label: z.string().or(z.null()),
  })
  .and(
    z.union([
      z.object({
        type: z.literal("CATEGORICAL"),
        queryableGranularities: z.null(),
      }),
      z.object({
        type: z.literal("TIME"),
        queryableGranularities: z.array(z.string()),
      }),
    ]),
  );
export type DbtMetricDimensionV2 = z.infer<typeof DbtMetricDimensionV2>;

export const DbtMetric = z.object({
  dbtUniqueId: z.string(),
  name: z.string(),
  displayName: z.string(),
  packageName: z.string().or(z.null()),
  dbtRunId: z.string(),
  description: z.string().or(z.null()),
  type: z.string().or(z.null()),
  timeGrains: DbtMetricTimeGrains,
  dimensions: z.array(DbtMetricDimensionV2),
  dbtModelName: z.string().or(z.null()),
  dbtModelUniqueId: z.string(),
  // eslint-disable-next-line hex/no-non-json-zod-in-wire-schema -- the same `InstanceOf(Date)` check the runtypes schema made; these are in-process Date values, not a JSON payload
  lastExecution: z.instanceof(Date),
  // eslint-disable-next-line hex/no-non-json-zod-in-wire-schema -- the same `InstanceOf(Date)` check the runtypes schema made; these are in-process Date values, not a JSON payload
  lastFetched: z.instanceof(Date),
});
export type DbtMetric = z.infer<typeof DbtMetric>;

export const OAuthLogicViewSettingLiteral = z.enum([
  "DISABLE_TOKEN_SHARING",
  "ENABLE_TOKEN_SHARING",
]);
export type OAuthLogicViewSetting = z.infer<
  typeof OAuthLogicViewSettingLiteral
>;
export const OAuthLogicViewSetting = getNormalEnum(
  OAuthLogicViewSettingLiteral,
);

export const OAuthPublishedAppSettingLiteral = z.enum([
  "DISABLE_TOKEN_SHARING",
  "ENABLE_TOKEN_SHARING",
]);
export type OAuthPublishedAppSetting = z.infer<
  typeof OAuthPublishedAppSettingLiteral
>;
export const OAuthPublishedAppSetting = getNormalEnum(
  OAuthPublishedAppSettingLiteral,
);

export const OAuthUserScopedResultsSettingLiteral = z.enum([
  "DISABLED",
  "ENABLED",
]);
export type OAuthUserScopedResultsSetting = z.infer<
  typeof OAuthUserScopedResultsSettingLiteral
>;
export const OAuthUserScopedResultsSetting = getNormalEnum(
  OAuthUserScopedResultsSettingLiteral,
);

export const DataConnectionAuthType = {
  USERNAME_AND_PASSWORD: "usernameAndPassword",
  OAUTH: "oauth",
  KEY_PAIR: "keyPair",
  CERTIFICATE: "certificate",
  SERVICE_ACCOUNT: "serviceAccount",
  ACCESS_TOKEN: "accessToken",
  IAM_ROLE: "iamRole",
};
export type DataConnectionAuthType = EnumValues<typeof DataConnectionAuthType>;

export const OAuthCredentialSettingLiteral = z.enum([
  "WORKSPACE",
  "USER_WITH_WORKSPACE_FALLBACK",
  "USER",
  "SERVICE_ACCOUNT",
  "SSO_TOKEN",
]);
export type OAuthCredentialSetting = z.infer<
  typeof OAuthCredentialSettingLiteral
>;
export const OAuthCredentialSetting = getNormalEnum(
  OAuthCredentialSettingLiteral,
);

export const DataConnectionSensitivity = {
  STANDARD: "STANDARD",
  SENSITIVE: "SENSITIVE",
} as const;
export type DataConnectionSensitivity = EnumValues<
  typeof DataConnectionSensitivity
>;

/**
 * Used to determine status of a data source after a refresh and let the user
 * know if it potentially may not exist anymore. This does not determine the * status of a refresh.
 */
export const DataSourceStatusLiteral = z.enum(["ORPHANED", "DEPRECATED"]);
export type DataSourceStatus = z.infer<typeof DataSourceStatusLiteral>;
export const DataSourceStatus = getNormalEnum(DataSourceStatusLiteral);

export const DbtSemanticLayerVersionLiteral = z.enum([
  "DBT_SEMANTIC_LAYER_V1",
  "DBT_SEMANTIC_LAYER_V2",
]);
export type DbtSemanticLayerVersion = z.infer<
  typeof DbtSemanticLayerVersionLiteral
>;
export const DbtSemanticLayerVersion = getNormalEnum(
  DbtSemanticLayerVersionLiteral,
);

export const DataConnectionRateLimitTypeLiteral = z.union([
  DataConnectionTypeLiteral,
  z.literal("default"),
  z.literal("snowflake+dbt"),
]);
export const DataConnectionRateLimits = zKeyedRecord(
  DataConnectionRateLimitTypeLiteral,
  z.number().optional(),
);
export type DataConnectionRateLimits = z.infer<typeof DataConnectionRateLimits>;

export const FilterMatchLiteral = z.enum([
  "EXACT",
  "PREFIX",
  "REGEX",
  "SUFFIX",
]);
export type FilterMatch = z.infer<typeof FilterMatchLiteral>;
export const FilterMatch = getNormalEnum(FilterMatchLiteral);

export const FilterModeLiteral = z.enum(["EXCLUDE", "INCLUDE"]);
export type FilterMode = z.infer<typeof FilterModeLiteral>;
export const FilterMode = getNormalEnum(FilterModeLiteral);

export const StringOrRegexFilter = z.object({
  match: FilterMatchLiteral,
  mode: FilterModeLiteral,
  options: z.array(z.string()),
});
export type StringOrRegexFilter = z.infer<typeof StringOrRegexFilter>;

/*
 * Replacing the original string[] filters with StringOrRegexFilter which encodes more information like
 * filter mode (include or exclude) and filter match (exact, prefix, suffix, regex). This information is needed
 * in order to support these mode and match options in the UI since it is not possible to differentiate between
 * a "starts with x" filter and a plain regex filter that happens to match.
 *
 * The string[] filters are now considered deprecated, but are retained to support a few existing customers
 * which had schema filtering configured prior to this change.
 */
export const SchemaFilter = z.object({
  catalogFilters: z.array(z.string()).optional(), // deprecated
  schemaFilters: z.array(z.string()).optional(), // deprecated
  tableFilters: z.array(z.string()).optional(), // deprecated
  columnFilters: z.array(z.string()).optional(), // deprecated
  databaseFilter: StringOrRegexFilter.optional(),
  schemaFilter: StringOrRegexFilter.optional(),
  tableFilter: StringOrRegexFilter.optional(),
});
export type SchemaFilter = z.infer<typeof SchemaFilter>;

export const DataSourceGrantEntityTypeLiteral = z.enum([
  "DATABASE",
  "SCHEMA",
  "TABLE",
  "COLUMN",
]);

export type DataSourceGrantEntityType = z.infer<
  typeof DataSourceGrantEntityTypeLiteral
>;
export const DataSourceGrantEntityType = getNormalEnum(
  DataSourceGrantEntityTypeLiteral,
);

export type GrantPrivileges = {
  literalPrivileges?: string[];
  privilegeGrantingPrincipals?: string[];
};
export type DataSourceGrantEntityId =
  | DataSourceTableId
  | DataSourceSchemaId
  | DataSourceDatabaseId;

export type ForceFullSyncOptions = {
  elasticsearch?: {
    enabled: boolean;
  };
};

// Type of the table in the underlying DWH. Currently used for Databricks
// metric views and Snowflake semantic views; may grow over time.
export const TableTypeLiteral = z.enum(["METRIC_VIEW", "SEMANTIC_VIEW"]);
export type TableType = z.infer<typeof TableTypeLiteral>;
export const TableType = getNormalEnum(TableTypeLiteral);

export const ColumnSemanticLayerTypeLiteral = z.enum([
  "MEASURE",
  "DIMENSION",
  "FACT",
]);
export type ColumnSemanticLayerType = z.infer<
  typeof ColumnSemanticLayerTypeLiteral
>;
export const ColumnSemanticLayerType = getNormalEnum(
  ColumnSemanticLayerTypeLiteral,
);
